import { useGetCompanies } from '@services/companies'
import { useAirportOperationsSummaryRpt } from '@services/report'
import { IconAirplaneCircle } from '@shared/icons'
import {
  convertTimestampToMinutes,
  toThousandSeparator,
} from '@shared/innmaxLib/utils/webHelper'
import { Col, Divider, Row } from 'antd'
import moment from 'moment'
import { pathOr } from 'ramda'
import React from 'react'
import MobilePageDrawer, {
  IMobilePageDrawerProps,
} from 'src/components/MobilePageDrawer'
import { IQueryMode } from '.'

const DataRow = ({
  label,
  value,
}: {
  label: string
  value?: number | string
}) => (
  <div className="flex text-primary gap-10 my-3">
    <label>{label}</label>
    <label>|</label>
    <label className="text-white">{value}</label>
  </div>
)

const SummaryPage = (
  props: Omit<IMobilePageDrawerProps, 'children'> & {
    queryMode: IQueryMode
    query: any
    isTriggerSearch?: boolean
  }
) => {
  const { queryMode, query, isTriggerSearch, ...others } = props

  const { byId } = useGetCompanies()

  const {
    data: summaryData,
    refetch: refetchSummary,
    loading: summaryLoading,
  } = useAirportOperationsSummaryRpt(query)

  React.useEffect(() => {
    if (!isTriggerSearch) {
      return
    }
    if (props.open) {
      refetchSummary(query)
    }
  }, [props.open, isTriggerSearch])

  const summaryDataValue = (key: string): any =>
    pathOr('--', [key], summaryData)

  return (
    <MobilePageDrawer title={'總計'} loading={summaryLoading} {...others}>
      {isTriggerSearch ? (
        <>
          <div className="flex justify-between text-lg items-baseline">
            <div className="text-white flex items-center gap-5">
              {queryMode === 'rawData' && (
                <div className="text-primary">
                  <IconAirplaneCircle />
                </div>
              )}
              <div>{byId[query.orgSessionId]?.name || '總表'}</div>
            </div>
            <div className="text-primary text-sm">
              {`${moment(query.startAt).format('YYYY/MM/DD')}~${moment(
                query.endAt
              ).format('YYYY/MM/DD')}`}
            </div>
          </div>
          <div className="bg-[rgba(65,164,255,0.1)] rounded-sm p-15 mt-20">
            <DataRow
              label="實際費用"
              value={toThousandSeparator(summaryDataValue('totalAmount'))}
            />
            {queryMode === 'summary' && (
              <div className="text-white opacity-50 text-sm mt-5">
                充電總費用+佔用費用-優惠費用+短溢收
              </div>
            )}
            <Divider className="border-primary" />
            <div className="text-white opacity-50 text-sm mb-5">充電度數</div>
            <Row>
              <Col span={12}>
                <DataRow
                  label="一般時段"
                  value={toThousandSeparator(summaryDataValue('offPeakFee'))}
                />
              </Col>
              <Col span={12}>
                <DataRow
                  label="壅塞時段"
                  value={toThousandSeparator(summaryDataValue('peakFee'))}
                />
              </Col>
            </Row>
            <DataRow
              label="充電總度數"
              value={toThousandSeparator(summaryDataValue('chargingDegree'))}
            />
            <DataRow
              label="充電總費用"
              value={toThousandSeparator(summaryDataValue('chargeFee'))}
            />
            <Divider className="border-primary" />

            <DataRow
              label="佔用費用"
              value={toThousandSeparator(summaryDataValue('occupationFee'))}
            />
            <DataRow
              label="佔用時數/分"
              value={toThousandSeparator(
                convertTimestampToMinutes(
                  summaryDataValue('occupationDuration')
                )
              )}
            />
            {queryMode === 'summary' && (
              <DataRow
                label="優惠費率"
                value={toThousandSeparator(summaryDataValue('discountFee'))}
              />
            )}
            <DataRow
              label="優惠費用"
              value={toThousandSeparator(summaryDataValue('discountAmount'))}
            />
            <DataRow
              label="短溢收"
              value={toThousandSeparator(summaryDataValue('adjustAmount'))}
            />

            {queryMode === 'rawData' && (
              <>
                <Divider className="border-primary" />

                <div className="text-white opacity-50 text-sm mb-5">
                  會計科目
                </div>
                <DataRow
                  label="電費分攤"
                  value={toThousandSeparator(
                    summaryDataValue('electricityCost')
                  )}
                />
                <DataRow
                  label="維護費分攤"
                  value={toThousandSeparator(
                    summaryDataValue('maintenanceCost')
                  )}
                />
                <DataRow
                  label="外包費分攤"
                  value={toThousandSeparator(
                    summaryDataValue('outsourcingCost')
                  )}
                />
              </>
            )}
          </div>
        </>
      ) : (
        <div className="flex justify-center items-center h-[200px]">
          <span className="text-secondary">請先執行搜尋</span>
        </div>
      )}
    </MobilePageDrawer>
  )
}

export default SummaryPage
